import {
  Box,
  Button,
  IconButton,
  Menu,
  Typography,
  withStyles,
} from "@material-ui/core"
import {
  IconCopy,
  IconDotsVertical,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react"
import { Suspense, lazy, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { useHistory } from "react-router-dom"
import { isSuper } from "../../../config"
import { useAuth } from "../../../contexts/AuthContext"
import { copyToClipboard } from "../../../utils"
import useApxorClient from "../../../utils/use-apxor-client"
import { useDeepCompareMemo } from "../../../utils/use-deep-compare"
import { ORG_DIALOG_STATES_ENUM } from "../../App/constants"
import { apxTheme } from "../../ApplicationFrame/styling/CustomCSS"
import { ROLES_ENUM } from "../../Dashboard/components/settings/constants"
import HStack from "../../ReusableComponents/HStack"
import VStack from "../../ReusableComponents/VStack"
import WhitePaper from "../../ReusableComponents/WhitePaper"
import DialogLoader from "../../ReusableComponents/skeletons/DialogLoader"
import { useTrackedState } from "../store"

const EditOrgDetailsDialog = lazy(() => import("./EditOrgDetailsDialog"))
const DeleteOrgDialog = lazy(() => import("./DeleteOrgDialog"))

export const StyledOrgCard = withStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "space-between",
    padding: "20px 25px",
    minHeight: "220px",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      boxShadow: "0px 1px 40px rgba(181, 202, 224, 0.4)",
    },
    cursor: "pointer",
  },
})(Box)

export const StyledMenu = withStyles({
  paper: {
    paddingBlock: "6px",
    paddingInline: "10px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: apxTheme.shadows[10],
  },
  list: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
})(Menu)

export const StyledMenuButton = withStyles({
  root: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    paddingBlock: "6px",
    paddingInline: "8px",
    borderLeft: "none",
    "&:hover": {
      backgroundColor: apxTheme.palette.primary.almostInvisible,
    },
  },
})(Button)

const StyledSkeletonBox = withStyles({
  "@keyframes pulse": {
    "50%": {
      opacity: "0.55",
    },
  },
  root: {
    animation: "$pulse 1.4s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  },
})(Box)

export function OrgCardSkeleton() {
  return (
    <StyledOrgCard component={WhitePaper}>
      <HStack spacing="16px">
        <StyledSkeletonBox
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            backgroundColor: "#E0E0E0",
          }}
        ></StyledSkeletonBox>
        <StyledSkeletonBox
          style={{
            backgroundColor: "#E0E0E0",
            padding: "10px",
            borderRadius: "3px",
            width: "100px",
          }}
        ></StyledSkeletonBox>
      </HStack>
      <HStack
        align="start"
        spacing="40px"
      >
        {[...Array(2)].map((_, idx) => (
          <VStack
            spacing="6px"
            key={idx}
          >
            <StyledSkeletonBox
              style={{
                backgroundColor: "#E0E0E0",
                padding: "10px",
                borderRadius: "3px",
                width: "26px",
              }}
            ></StyledSkeletonBox>
            <StyledSkeletonBox
              style={{
                backgroundColor: "#E0E0E0",
                padding: "15px 10px",
                borderRadius: "3px",
                width: "60px",
              }}
            ></StyledSkeletonBox>
          </VStack>
        ))}
      </HStack>
    </StyledOrgCard>
  )
}

export default function OrgCard({ org }) {
  const { push } = useHistory()
  const [cardHovered, setCardHovered] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const { org_data } = useTrackedState()

  const [dialogState, setDialogState] = useState(ORG_DIALOG_STATES_ENUM.CLOSE)

  const handleClick = (event) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (event) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const { user } = useAuth()

  const currentUserFromOrgState = useDeepCompareMemo(() => {
    return {
      customer_id: user?.email,
      ...((isSuper(user?.email) || user?.email === org_data?.owner) && {
        role: ROLES_ENUM.ORG_OWNER.value,
      }),
    }
  }, [org_data, user])

  const userHasAccessToMutateOrg = useMemo(() => {
    return currentUserFromOrgState?.role === ROLES_ENUM.ORG_OWNER.value
  }, [currentUserFromOrgState.role])

  const { logEvent } = useApxorClient()

  useEffect(() => {
    const handleMouseOut = (event) => {
      if (
        event.clientY <= 0 ||
        event.clientX <= 0 ||
        event.clientX >= window.innerWidth ||
        event.clientY >= window.innerHeight
      ) {
        handleClose(event)
      }
    }
    window.addEventListener("mouseout", handleMouseOut)

    return () => {
      window.removeEventListener("mouseout", handleMouseOut)
    }
  }, [])

  return (
    <>
      <StyledOrgCard
        component={WhitePaper}
        style={{
          position: "relative",
        }}
        onClick={() => {
          push(`/orgs/${org?._id}/apps`)
          logEvent("Apps_PageLaunched", { source: "Orgs-page" })
        }}
        onMouseEnter={() => {
          setCardHovered(true)
        }}
        onMouseLeave={() => {
          setCardHovered(false)
        }}
      >
        {cardHovered && (
          <>
            {/* Org Actions Menu Trigger */}
            <IconButton
              style={{
                position: "absolute",
                top: "12px",
                right: "8px",
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleClick(e)
              }}
            >
              <IconDotsVertical
                size={18}
                color={apxTheme.palette.action.active}
              />
            </IconButton>

            {/* Org Actions Menu */}
            <StyledMenu
              elevation={0}
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {/* Copy Org ID Button */}
              <StyledMenuButton
                onClick={(e) => {
                  copyToClipboard(org?._id)
                  handleClose(e)
                  toast.success("Org ID copied to clipboard successfully!")
                }}
                startIcon={
                  <IconCopy
                    color={apxTheme.palette.text.primary}
                    size={18}
                  />
                }
              >
                Copy Org ID
              </StyledMenuButton>

              {/* Edit Org Button */}
              {userHasAccessToMutateOrg && (
                <StyledMenuButton
                  onClick={(e) => {
                    setDialogState(ORG_DIALOG_STATES_ENUM.EDIT)
                    handleClose(e)
                  }}
                  startIcon={
                    <IconEdit
                      color={apxTheme.palette.primary.main}
                      size={18}
                    />
                  }
                >
                  Edit Org Details
                </StyledMenuButton>
              )}

              {/* Delete Org Button */}
              {userHasAccessToMutateOrg && (
                <StyledMenuButton
                  onClick={(e) => {
                    setDialogState(ORG_DIALOG_STATES_ENUM.DELETE)
                    handleClose(e)
                  }}
                  startIcon={
                    <IconTrash
                      color={apxTheme.palette.error.main}
                      size={18}
                    />
                  }
                >
                  Delete Org
                </StyledMenuButton>
              )}
            </StyledMenu>
          </>
        )}

        <HStack
          align="start"
          spacing="16px"
        >
          <Box
            style={{
              maxWidth: "60px",
            }}
          >
            <img
              src={org?.icon_url}
              height="60px"
              width="60px"
              alt=""
            />
          </Box>
          <Typography
            style={{
              color: "#002845",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            {org?.name}
          </Typography>
        </HStack>
        <HStack
          spacing="40px"
          align="start"
        >
          <VStack spacing="16px">
            <Typography
              style={{
                fontSize: "12px",
                fontWeight: "400",
                color: "rgba(0, 40, 69, 0.7)",
              }}
            >
              Admin
            </Typography>
            <Typography
              styles={{
                color: "#002845",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              {org?.owner}
            </Typography>
          </VStack>
        </HStack>
      </StyledOrgCard>

      {/* Edit Org Dialog */}
      {dialogState === ORG_DIALOG_STATES_ENUM.EDIT && (
        <Suspense fallback={<DialogLoader />}>
          <EditOrgDetailsDialog
            open={dialogState === ORG_DIALOG_STATES_ENUM.EDIT}
            onClose={() => {
              setDialogState(ORG_DIALOG_STATES_ENUM.CLOSE)
            }}
            org={org}
          />
        </Suspense>
      )}

      {/* Delete Org Dialog */}
      {dialogState === ORG_DIALOG_STATES_ENUM.DELETE && (
        <Suspense fallback={<DialogLoader />}>
          <DeleteOrgDialog
            open={dialogState === ORG_DIALOG_STATES_ENUM.DELETE}
            onClose={() => {
              setDialogState(ORG_DIALOG_STATES_ENUM.CLOSE)
            }}
            org={org}
          />
        </Suspense>
      )}
    </>
  )
}
